import { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { apiGet } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import { checkAndClearPeriodCache } from '../../services/offline'
import {
  getActiveTimer,
  getActiveBreak,
  startTimer,
  pauseTimer,
  resumeTimer,
  stopTimer,
  ActiveTimer,
  ActiveBreak,
} from '../../services/eagleVision'
import { useAttendance } from '../../hooks/useAttendance'
import { usePermissions } from '../../hooks/usePermissions'
import CameraCapture from '../../components/common/CameraCapture'
import PullToRefresh from '../../components/common/PullToRefresh'
import {
  Calendar,
  Target,
  TrendingUp,
  CheckCircle,
  Clock,
  ChevronRight,
  FileText,
  User,
  Users,
  ClipboardCheck,
  FileSpreadsheet,
  Package,
  CalendarClock,
  GraduationCap,
  XCircle,
  Loader2,
  MapPin,
  Play,
  Pause,
  Square,
  Coffee,
  Timer,
  Camera,
  X,
} from 'lucide-react'

interface Period {
  id: number
  name: string
  year: number
  month: number
}

interface ScoreData {
  total_score: number
  is_eligible: boolean
  scores: Array<{
    indicator: {
      id: number
      code: string
      name: string
      max_points: number
      target: number
    }
    submission_count: number
    calculated_score: number
  }>
}

interface Submission {
  id: number
  indicator_id: number
  submission_date: string
  status: string
  notes?: string
  indicator?: {
    code: string
    name: string
  }
}

export default function PersonalDashboard() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { can, canAccessPage, canAny, hasModule } = usePermissions()
  const kpiEnabled = hasModule('kpi')
  const [period, setPeriod] = useState<Period | null>(null)
  const [scoreData, setScoreData] = useState<ScoreData | null>(null)
  const [recentSubmissions, setRecentSubmissions] = useState<Submission[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Attendance hook
  const attendance = useAttendance()
  const [cameraAction, setCameraAction] = useState<'checkin' | 'checkout' | null>(null)
  const [cameraReady, setCameraReady] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)

  // Time Tracker state
  const [activeTimerData, setActiveTimerData] = useState<ActiveTimer | null>(null)
  const [activeBreakData, setActiveBreakData] = useState<ActiveBreak | null>(null)
  const [timerLoading, setTimerLoading] = useState(true)
  const [timerAction, setTimerAction] = useState(false)
  const [timerError, setTimerError] = useState<string | null>(null)
  const [timerTaskId, setTimerTaskId] = useState('')
  const [timerMemo, setTimerMemo] = useState('')
  const [timerImage, setTimerImage] = useState<string | null>(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good Morning'
    if (hour < 17) return 'Good Afternoon'
    return 'Good Evening'
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (kpiEnabled) {
          // Get current period
          const periods = await apiGet<Period[]>('/kpi/periods').catch(() => [])
          const currentPeriod = Array.isArray(periods) && periods.length > 0 ? periods[0] : null
          setPeriod(currentPeriod)

          // Check if period changed and clear caches if needed
          if (currentPeriod) {
            const cacheCleared = await checkAndClearPeriodCache(currentPeriod.id)
            if (cacheCleared) {
              console.log('New period detected, caches cleared')
            }
          }

          // Get scores if period exists
          if (currentPeriod) {
            const scores = await apiGet<ScoreData>('/kpi/scores', {
              period_id: currentPeriod.id
            }).catch(() => null)
            if (scores && typeof scores === 'object' && 'total_score' in scores) {
              setScoreData(scores)
            }
          }

          // Get recent submissions for current period only
          if (currentPeriod) {
            const subs = await apiGet<Submission[]>('/kpi/submissions', {
              period_id: currentPeriod.id
            }).catch(() => [])
            if (Array.isArray(subs)) {
              setRecentSubmissions(subs.slice(0, 5))
            }
          }
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [kpiEnabled])

  // Fetch active timer on mount
  const fetchTimerState = useCallback(async () => {
    try {
      const [timerRes, breakRes] = await Promise.all([
        getActiveTimer().catch(() => null),
        getActiveBreak().catch(() => null),
      ])
      // EV API may return { data: {...} } or the object directly
      if (timerRes) {
        const timer = timerRes.data ?? (timerRes as unknown as ActiveTimer)
        setActiveTimerData(timer && typeof timer === 'object' && 'id' in timer ? timer : null)
      } else {
        setActiveTimerData(null)
      }
      if (breakRes) {
        const brk = breakRes.data ?? (breakRes as unknown as ActiveBreak)
        setActiveBreakData(brk && typeof brk === 'object' && 'id' in brk ? brk : null)
      } else {
        setActiveBreakData(null)
      }
    } catch {
      setActiveTimerData(null)
      setActiveBreakData(null)
    } finally {
      setTimerLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTimerState()
  }, [fetchTimerState])

  // Live elapsed time counter
  useEffect(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current)
      timerIntervalRef.current = null
    }

    if (activeTimerData?.start_time && !activeBreakData) {
      const updateElapsed = () => {
        const start = new Date(activeTimerData.start_time).getTime()
        const now = Date.now()
        setElapsedSeconds(Math.floor((now - start) / 1000))
      }
      updateElapsed()
      timerIntervalRef.current = setInterval(updateElapsed, 1000)
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
      }
    }
  }, [activeTimerData, activeBreakData])

  const openCamera = async (action: 'checkin' | 'checkout') => {
    setCameraAction(action)
    setCameraReady(false)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      })
      streamRef.current = stream
      // Wait for videoRef to be rendered
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          videoRef.current.play()
          setCameraReady(true)
        }
      }, 100)
    } catch {
      // Camera not available, submit without image
      closeCamera()
      if (action === 'checkin') attendance.handleCheckIn()
      else attendance.handleCheckOut()
    }
  }

  const closeCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }
    setCameraAction(null)
    setCameraReady(false)
  }

  const captureAndSubmit = () => {
    const video = videoRef.current
    const action = cameraAction
    if (!video || !action) return

    // Capture frame from video (small size for EV API limit)
    const canvas = document.createElement('canvas')
    canvas.width = Math.min(video.videoWidth, 320)
    canvas.height = Math.min(video.videoHeight, 240)
    const ctx = canvas.getContext('2d')
    if (!ctx) { closeCamera(); return }

    // Mirror the image (selfie cameras are mirrored in preview)
    ctx.translate(canvas.width, 0)
    ctx.scale(-1, 1)
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

    const base64 = canvas.toDataURL('image/jpeg', 0.4).split(',')[1]

    closeCamera()
    if (action === 'checkin') attendance.handleCheckIn(base64)
    else attendance.handleCheckOut(base64)
  }

  const formatElapsed = (seconds: number) => {
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = seconds % 60
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }

  const handleStartTimer = async () => {
    if (!timerTaskId.trim() || !timerMemo.trim()) return
    setTimerAction(true)
    setTimerError(null)
    try {
      await startTimer(
        timerTaskId.trim(),
        timerMemo.trim(),
        undefined,
        attendance.location?.lat,
        attendance.location?.lng,
        timerImage || undefined
      )
      setTimerTaskId('')
      setTimerMemo('')
      setTimerImage(null)
      await fetchTimerState()
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
        : undefined
      setTimerError(msg || 'Failed to start timer')
    } finally {
      setTimerAction(false)
    }
  }

  const handlePauseTimer = async () => {
    if (!activeTimerData) return
    setTimerAction(true)
    setTimerError(null)
    try {
      await pauseTimer(activeTimerData.id)
      await fetchTimerState()
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
        : undefined
      setTimerError(msg || 'Failed to pause timer')
    } finally {
      setTimerAction(false)
    }
  }

  const handleResumeTimer = async () => {
    if (!activeBreakData) return
    setTimerAction(true)
    setTimerError(null)
    try {
      await resumeTimer(activeBreakData.id)
      await fetchTimerState()
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
        : undefined
      setTimerError(msg || 'Failed to resume timer')
    } finally {
      setTimerAction(false)
    }
  }

  const handleStopTimer = async () => {
    if (!activeTimerData) return
    setTimerAction(true)
    setTimerError(null)
    try {
      await stopTimer(
        activeTimerData.id,
        attendance.location?.lat,
        attendance.location?.lng,
        timerImage || undefined
      )
      setTimerImage(null)
      setActiveTimerData(null)
      setActiveBreakData(null)
      setElapsedSeconds(0)
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
        : undefined
      setTimerError(msg || 'Failed to stop timer')
    } finally {
      setTimerAction(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'present': return 'text-green-600 bg-green-100'
      case 'absent': return 'text-red-600 bg-red-100'
      case 'late': return 'text-yellow-600 bg-yellow-100'
      case 'on_leave': return 'text-blue-600 bg-blue-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const totalSubmissions = scoreData?.scores?.reduce((sum, s) => sum + s.submission_count, 0) || 0
  const completedIndicators = scoreData?.scores?.filter(s => s.calculated_score >= s.indicator.max_points).length || 0
  const totalIndicators = scoreData?.scores?.length || 0

  const refreshAll = useCallback(async () => {
    const fetchData = async () => {
      if (kpiEnabled) {
        const periods = await apiGet<Period[]>('/kpi/periods').catch(() => [])
        const currentPeriod = Array.isArray(periods) && periods.length > 0 ? periods[0] : null
        setPeriod(currentPeriod)
        if (currentPeriod) {
          const scores = await apiGet<ScoreData>('/kpi/scores', { period_id: currentPeriod.id }).catch(() => null)
          if (scores && typeof scores === 'object' && 'total_score' in scores) {
            setScoreData(scores)
          }
          const subs = await apiGet<Submission[]>('/kpi/submissions', { period_id: currentPeriod.id }).catch(() => [])
          if (Array.isArray(subs)) {
            setRecentSubmissions(subs.slice(0, 5))
          }
        }
      }
    }
    await Promise.all([fetchData(), fetchTimerState(), attendance.fetchSummary()])
  }, [kpiEnabled, fetchTimerState, attendance])

  // Auto-dismiss timer error after 5s
  useEffect(() => {
    if (timerError) {
      const t = setTimeout(() => setTimerError(null), 5000)
      return () => clearTimeout(t)
    }
  }, [timerError])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent"></div>
      </div>
    )
  }

  return (
    <PullToRefresh onRefresh={refreshAll}>
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header with Score Card inside */}
      <div className="bg-gradient-to-br from-primary-500 to-primary-700 text-white px-4 pt-8 pb-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-primary-100 text-sm">{getGreeting()}</p>
            <h1 className="text-xl font-bold">{user?.name || 'User'}</h1>
          </div>
          <button
            onClick={() => navigate('/profile')}
            className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center"
          >
            <User className="h-5 w-5" />
          </button>
        </div>

        {/* Period Info */}
        {kpiEnabled && (
        <div className="flex items-center gap-2 text-primary-100 text-sm mb-4">
          <Calendar className="h-4 w-4" />
          <span>{period?.name || 'No active period'}</span>
        </div>
        )}

        {/* Score Card - inside header */}
        {canAny([{ module: 'pwa_kpi_hub' }, { module: 'pwa_kpi_submission' }, { module: 'pwa_scores' }]) && (
        <div className="bg-white rounded-2xl shadow-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-gray-500 text-sm">Total Score</p>
              <p className="text-3xl font-bold text-gray-900">
                {scoreData?.total_score?.toFixed(1) || '0'}
                <span className="text-lg text-gray-400 font-normal"> / 100</span>
              </p>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              scoreData?.is_eligible
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {scoreData?.is_eligible ? 'Eligible' : 'Not Eligible'}
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
            <div
              className={`h-3 rounded-full transition-all ${
                (scoreData?.total_score || 0) >= 80 ? 'bg-green-500' :
                (scoreData?.total_score || 0) >= 50 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${Math.min(scoreData?.total_score || 0, 100)}%` }}
            />
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-3 gap-4 pt-2 border-t">
            <div className="text-center">
              <p className="text-xl font-bold text-gray-900">{totalSubmissions}</p>
              <p className="text-xs text-gray-500">Submissions</p>
            </div>
            <div className="text-center border-x">
              <p className="text-xl font-bold text-gray-900">{completedIndicators}/{totalIndicators}</p>
              <p className="text-xs text-gray-500">Completed</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-gray-900">80</p>
              <p className="text-xs text-gray-500">Min. Score</p>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Attendance Card */}
      {canAccessPage('pwa_attendance') && (
        <div className="px-4 mt-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Today's Attendance</h2>
            <button
              onClick={() => navigate('/attendance')}
              className="text-primary-600 text-sm font-medium flex items-center"
            >
              Details <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            {attendance.needsAuth ? (
              <div className="text-center py-4">
                <Clock className="h-10 w-10 text-yellow-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700">Eagle Vision session expired</p>
                <p className="text-xs text-gray-500 mt-1">Please logout and login again to access attendance.</p>
              </div>
            ) : (
            <>
            {/* Date + Status */}
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-gray-500">{format(new Date(), 'EEEE, MMM d')}</p>
              <div className="flex items-center gap-2">
                {attendance.summary?.status && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(attendance.summary.status)}`}>
                    {attendance.summary.status.replace('_', ' ').toUpperCase()}
                  </span>
                )}
                {attendance.summary?.shift && (
                  <span className="text-xs text-gray-500">
                    {attendance.summary.shift.start_time} - {attendance.summary.shift.end_time}
                  </span>
                )}
              </div>
            </div>

            {/* Check-in/out times */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="bg-gray-50 rounded-lg p-2.5">
                <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-0.5">
                  <Clock className="h-3.5 w-3.5" />
                  Check In
                </div>
                <p className="font-semibold text-base">
                  {attendance.displayCheckIn ? format(new Date(attendance.displayCheckIn), 'HH:mm') : '--:--'}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2.5">
                <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-0.5">
                  <Clock className="h-3.5 w-3.5" />
                  Check Out
                </div>
                <p className="font-semibold text-base">
                  {attendance.displayCheckOut ? format(new Date(attendance.displayCheckOut), 'HH:mm') : '--:--'}
                </p>
              </div>
            </div>

            {/* Working hours (if completed) */}
            {attendance.summary?.working_hours !== undefined && attendance.summary.working_hours > 0 && (
              <div className="text-center py-1.5 bg-green-50 rounded-lg mb-3">
                <p className="text-xs text-green-700">
                  Working Hours: <span className="font-semibold">{attendance.summary.working_hours.toFixed(2)} hrs</span>
                </p>
              </div>
            )}

            {/* Result Notification */}
            {attendance.result && (
              <div
                className={`mb-3 p-3 rounded-lg border text-sm ${
                  attendance.result.type === 'success'
                    ? 'bg-green-50 border-green-200 text-green-700'
                    : 'bg-red-50 border-red-200 text-red-700'
                }`}
              >
                <div className="flex items-center gap-2">
                  {attendance.result.type === 'success' ? (
                    <CheckCircle className="h-4 w-4 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 flex-shrink-0" />
                  )}
                  <span className="font-medium">{attendance.result.title}</span>
                  <button onClick={() => attendance.setResult(null)} className="ml-auto">
                    <XCircle className="h-3.5 w-3.5 opacity-50" />
                  </button>
                </div>
                <p className="text-xs mt-1 ml-6">{attendance.result.message}</p>
              </div>
            )}

            {/* GPS Status */}
            <div className="flex items-center gap-1.5 mb-3">
              <MapPin className="h-3.5 w-3.5 text-gray-400" />
              {attendance.locationLoading ? (
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Getting GPS...
                </span>
              ) : attendance.location ? (
                <span className="text-xs text-green-600">
                  GPS: {attendance.location.lat.toFixed(6)}, {attendance.location.lng.toFixed(6)}
                </span>
              ) : (
                <span className="text-xs text-red-600">
                  {attendance.locationError || 'GPS unavailable'}
                </span>
              )}
            </div>

            {/* Action button */}
            {!(attendance.hasCheckedIn && attendance.hasCheckedOut) ? (
              <div>
                {!attendance.hasCheckedIn && can('pwa_attendance', 'check_in') ? (
                  <button
                    onClick={() => openCamera('checkin')}
                    disabled={attendance.isCheckingIn || !!cameraAction || !attendance.location}
                    className={`w-full py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 ${
                      !attendance.location
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800'
                    }`}
                  >
                    {attendance.isCheckingIn ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : attendance.locationLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Camera className="h-4 w-4" />
                    )}
                    {attendance.isCheckingIn ? 'Checking In...' : attendance.locationLoading ? 'Waiting for GPS...' : !attendance.location ? 'GPS Required' : 'Check In'}
                  </button>
                ) : attendance.hasCheckedIn && !attendance.hasCheckedOut && can('pwa_attendance', 'check_out') ? (
                  <button
                    onClick={() => openCamera('checkout')}
                    disabled={attendance.isCheckingOut || !!cameraAction || !attendance.location}
                    className={`w-full py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 ${
                      !attendance.location
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800'
                    }`}
                  >
                    {attendance.isCheckingOut ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : attendance.locationLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Camera className="h-4 w-4" />
                    )}
                    {attendance.isCheckingOut ? 'Checking Out...' : attendance.locationLoading ? 'Waiting for GPS...' : !attendance.location ? 'GPS Required' : 'Check Out'}
                  </button>
                ) : null}
              </div>
            ) : (
              <div className="text-center py-2 bg-green-50 rounded-lg">
                <div className="flex items-center justify-center gap-1.5">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <p className="text-sm font-medium text-green-700">Attendance completed</p>
                </div>
              </div>
            )}
            </>
            )}
          </div>
        </div>
      )}

      {/* Time Tracker Section */}
      {canAccessPage('pwa_timelog') && (
      <div className="px-4 mt-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Time Tracker</h2>
            <button
              onClick={() => navigate('/attendance')}
              className="text-primary-600 text-sm font-medium flex items-center"
            >
              View History <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-4">
            {timerLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
              </div>
            ) : activeTimerData ? (
              /* Active timer view */
              <div>
                {/* Elapsed time display */}
                <div className="text-center mb-3">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Timer className={`h-5 w-5 ${activeBreakData ? 'text-yellow-500' : 'text-primary-600'}`} />
                    {activeBreakData && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                        On Break
                      </span>
                    )}
                  </div>
                  <p className={`text-3xl font-mono font-bold ${activeBreakData ? 'text-yellow-600' : 'text-gray-900'}`}>
                    {formatElapsed(elapsedSeconds)}
                  </p>
                </div>

                {/* Timer info */}
                <div className="bg-gray-50 rounded-lg p-3 mb-3 space-y-1">
                  {activeTimerData.task && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-500">Task:</span>
                      <span className="font-medium text-gray-900">{activeTimerData.task.name}</span>
                    </div>
                  )}
                  {activeTimerData.memo && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-500">Memo:</span>
                      <span className="text-gray-700">{activeTimerData.memo}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500">Started:</span>
                    <span className="text-gray-700">
                      {format(new Date(activeTimerData.start_time), 'HH:mm')}
                    </span>
                  </div>
                </div>

                {/* Error */}
                {timerError && (
                  <div className="mb-3 p-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-center gap-2">
                    <XCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{timerError}</span>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex items-center gap-2">
                  <CameraCapture
                    onCapture={(base64) => setTimerImage(base64)}
                    onClear={() => setTimerImage(null)}
                    disabled={timerAction}
                    compact
                  />
                  {activeBreakData ? (
                    <>
                      <button
                        onClick={handleResumeTimer}
                        disabled={timerAction}
                        className="flex-1 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 bg-green-600 text-white hover:bg-green-700 active:bg-green-800 transition-colors disabled:opacity-50"
                      >
                        {timerAction ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                        Resume
                      </button>
                      <button
                        onClick={handleStopTimer}
                        disabled={timerAction}
                        className="py-2.5 px-4 rounded-lg font-medium flex items-center justify-center gap-2 bg-red-600 text-white hover:bg-red-700 active:bg-red-800 transition-colors disabled:opacity-50"
                      >
                        {timerAction ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4" />}
                        Stop
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={handlePauseTimer}
                        disabled={timerAction}
                        className="flex-1 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 bg-yellow-500 text-white hover:bg-yellow-600 active:bg-yellow-700 transition-colors disabled:opacity-50"
                      >
                        {timerAction ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pause className="h-4 w-4" />}
                        Pause
                      </button>
                      <button
                        onClick={handleStopTimer}
                        disabled={timerAction}
                        className="py-2.5 px-4 rounded-lg font-medium flex items-center justify-center gap-2 bg-red-600 text-white hover:bg-red-700 active:bg-red-800 transition-colors disabled:opacity-50"
                      >
                        {timerAction ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4" />}
                        Stop
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              /* No active timer - start form */
              <div>
                <div className="flex items-center gap-2 text-gray-500 mb-4">
                  <Coffee className="h-5 w-5" />
                  <span className="text-sm">No active timer</span>
                </div>

                <div className="space-y-3 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Task ID <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={timerTaskId}
                      onChange={(e) => setTimerTaskId(e.target.value)}
                      placeholder="Enter task ID"
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Memo <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={timerMemo}
                      onChange={(e) => setTimerMemo(e.target.value)}
                      placeholder="What are you working on?"
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Error */}
                {timerError && (
                  <div className="mb-3 p-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-center gap-2">
                    <XCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{timerError}</span>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <CameraCapture
                    onCapture={(base64) => setTimerImage(base64)}
                    onClear={() => setTimerImage(null)}
                    disabled={timerAction}
                    compact
                  />
                  <button
                    onClick={handleStartTimer}
                    disabled={timerAction || !timerTaskId.trim() || !timerMemo.trim()}
                    className="flex-1 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 transition-colors disabled:opacity-50"
                  >
                    {timerAction ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                    {timerAction ? 'Starting...' : 'Start Timer'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* KPI Indicator Modules */}
      {canAccessPage('pwa_kpi_submission') && (
      <div className="px-4 mt-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">KPI Indicators</h2>
          <button
            onClick={() => navigate('/kpi')}
            className="text-primary-600 text-sm font-medium flex items-center"
          >
            View All <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {/* C.1 - Meeting */}
          <button
            onClick={() => navigate('/kpi/submit/c-1')}
            className="bg-white p-3 rounded-xl shadow-sm flex flex-col items-center gap-1 hover:bg-gray-50 transition-colors"
          >
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <span className="text-xs font-medium text-gray-900">C.1</span>
          </button>

          {/* C.2 - Timesheet */}
          <button
            onClick={() => navigate('/kpi/submit/c-2')}
            className="bg-white p-3 rounded-xl shadow-sm flex flex-col items-center gap-1 hover:bg-gray-50 transition-colors"
          >
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <ClipboardCheck className="h-5 w-5 text-green-600" />
            </div>
            <span className="text-xs font-medium text-gray-900">C.2</span>
          </button>

          {/* C.3 - Briefing */}
          <button
            onClick={() => navigate('/kpi/submit/c-3')}
            className="bg-white p-3 rounded-xl shadow-sm flex flex-col items-center gap-1 hover:bg-gray-50 transition-colors"
          >
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <Calendar className="h-5 w-5 text-purple-600" />
            </div>
            <span className="text-xs font-medium text-gray-900">C.3</span>
          </button>

          {/* C.4 - QAP */}
          <button
            onClick={() => navigate('/kpi/submit/c-4')}
            className="bg-white p-3 rounded-xl shadow-sm flex flex-col items-center gap-1 hover:bg-gray-50 transition-colors"
          >
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <FileSpreadsheet className="h-5 w-5 text-orange-600" />
            </div>
            <span className="text-xs font-medium text-gray-900">C.4</span>
          </button>

          {/* C.5 - Monthly Report */}
          <button
            onClick={() => navigate('/kpi/submit/c-5')}
            className="bg-white p-3 rounded-xl shadow-sm flex flex-col items-center gap-1 hover:bg-gray-50 transition-colors"
          >
            <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
              <FileText className="h-5 w-5 text-pink-600" />
            </div>
            <span className="text-xs font-medium text-gray-900">C.5</span>
          </button>

          {/* C.6 - Asset */}
          <button
            onClick={() => navigate('/kpi/submit/c-6')}
            className="bg-white p-3 rounded-xl shadow-sm flex flex-col items-center gap-1 hover:bg-gray-50 transition-colors"
          >
            <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
              <Package className="h-5 w-5 text-cyan-600" />
            </div>
            <span className="text-xs font-medium text-gray-900">C.6</span>
          </button>

          {/* C.7 - P3C */}
          <button
            onClick={() => navigate('/kpi/submit/c-7')}
            className="bg-white p-3 rounded-xl shadow-sm flex flex-col items-center gap-1 hover:bg-gray-50 transition-colors"
          >
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <CalendarClock className="h-5 w-5 text-amber-600" />
            </div>
            <span className="text-xs font-medium text-gray-900">C.7</span>
          </button>

          {/* C.8 - Training */}
          <button
            onClick={() => navigate('/kpi/submit/c-8')}
            className="bg-white p-3 rounded-xl shadow-sm flex flex-col items-center gap-1 hover:bg-gray-50 transition-colors"
          >
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
              <GraduationCap className="h-5 w-5 text-indigo-600" />
            </div>
            <span className="text-xs font-medium text-gray-900">C.8</span>
          </button>
        </div>
      </div>
      )}

      {/* KPI Progress */}
      {canAccessPage('pwa_scores') && scoreData?.scores && scoreData.scores.length > 0 && (
        <div className="px-4 mt-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">KPI Progress</h2>
            <span className="text-sm text-gray-500">
              {completedIndicators}/{totalIndicators} completed
            </span>
          </div>
          <div className="bg-white rounded-xl shadow-sm divide-y">
            {scoreData.scores.map(score => {
              const progress = (score.calculated_score / score.indicator.max_points) * 100
              const isComplete = progress >= 100
              const submissionProgress = score.indicator.target > 0
                ? `${score.submission_count}/${score.indicator.target}`
                : score.submission_count.toString()
              return (
                <button
                  key={score.indicator.code}
                  onClick={() => navigate(`/kpi/submit/${score.indicator.code.toLowerCase().replace('.', '-')}`)}
                  className="p-4 w-full text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {isComplete ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <Target className="h-5 w-5 text-gray-400" />
                      )}
                      <span className="font-medium text-gray-900">{score.indicator.code}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-medium text-gray-900">
                        {score.calculated_score.toFixed(1)} / {score.indicator.max_points}
                      </span>
                      <span className="text-xs text-gray-500 ml-2">
                        ({submissionProgress})
                      </span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${isComplete ? 'bg-green-500' : 'bg-primary-500'}`}
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1 truncate">{score.indicator.name}</p>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Recent Submissions */}
      {kpiEnabled && recentSubmissions.length > 0 && (
        <div className="px-4 mt-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Recent Submissions</h2>
          </div>
          <div className="bg-white rounded-xl shadow-sm divide-y">
            {recentSubmissions.map(sub => (
              <div key={sub.id} className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  sub.status === 'approved' ? 'bg-green-100' :
                  sub.status === 'rejected' ? 'bg-red-100' : 'bg-primary-100'
                }`}>
                  {sub.status === 'approved' ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : sub.status === 'rejected' ? (
                    <TrendingUp className="h-5 w-5 text-red-600" />
                  ) : (
                    <Clock className="h-5 w-5 text-primary-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {sub.indicator?.code || 'KPI'} - {sub.indicator?.name || 'Submission'}
                  </p>
                  <p className="text-sm text-gray-500">{sub.submission_date}</p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                  sub.status === 'approved' ? 'bg-green-100 text-green-700' :
                  sub.status === 'rejected' ? 'bg-red-100 text-red-700' :
                  'bg-primary-100 text-primary-700'
                }`}>
                  {sub.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {kpiEnabled && !period && (
        <div className="px-4 mt-6">
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Period</h3>
            <p className="text-gray-500">Contact your administrator to set up a KPI period.</p>
          </div>
        </div>
      )}

      {/* No modules enabled fallback */}
      {!kpiEnabled && !canAccessPage('pwa_attendance') && !canAccessPage('pwa_timelog') && (
        <div className="px-4 mt-6">
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Welcome</h3>
            <p className="text-gray-500">No modules are currently enabled. Contact your administrator.</p>
          </div>
        </div>
      )}

      {/* Selfie Camera Overlay */}
      {cameraAction && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-black/80">
            <span className="text-white text-sm font-medium">
              {cameraAction === 'checkin' ? 'Check In Selfie' : 'Check Out Selfie'}
            </span>
            <button onClick={closeCamera} className="text-white p-1">
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Video preview */}
          <div className="flex-1 flex items-center justify-center overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ transform: 'scaleX(-1)' }}
            />
          </div>

          {/* Capture button */}
          <div className="flex items-center justify-center py-6 bg-black/80">
            <button
              onClick={captureAndSubmit}
              disabled={!cameraReady}
              className="w-16 h-16 rounded-full border-4 border-white flex items-center justify-center disabled:opacity-30 active:scale-95 transition-transform"
            >
              <div className={`w-12 h-12 rounded-full ${cameraAction === 'checkin' ? 'bg-green-500' : 'bg-red-500'}`} />
            </button>
          </div>
        </div>
      )}
    </div>
    </PullToRefresh>
  )
}
